import { useTranslation } from 'react-i18next';
import { ProgressBar, mergeClasses } from '@fluentui/react-components';
import { useUtilizationCardStyles } from './UtilizationCard.styles';

/** Props for {@link UtilizationCard}. */
export interface UtilizationCardProps {
  /** Card title (e.g. "Meeting utilization"). */
  label: string;
  /** Utilization percentage (0–100). Ignored when `applicable` is false. */
  percentage: number;
  /** When false the card shows "N/A" (zero-denominator case). */
  applicable: boolean;
  /** Optional descriptive caption under the title. */
  caption?: string;
  /** Optional secondary detail line (e.g. booked vs available hours). */
  detail?: string;
}

/**
 * Renders a single utilization percentage with a Fluent {@link ProgressBar}.
 * When the underlying denominator is zero the value is not applicable and the
 * card shows "N/A" instead of a misleading 0%.
 */
export const UtilizationCard = ({
  label,
  percentage,
  applicable,
  caption,
  detail,
}: UtilizationCardProps) => {
  const styles = useUtilizationCardStyles();
  const { t } = useTranslation('metricsReport');

  const clamped = Math.max(0, Math.min(percentage, 100));

  return (
    <section className={styles.root} aria-label={label}>
      <div className={styles.header}>
        <h3 className={styles.label}>{label}</h3>
        {applicable ? (
          <span className={styles.percentage} data-testid="utilization-value">
            {clamped.toFixed(1)}%
          </span>
        ) : (
          <span
            className={mergeClasses(styles.percentage, styles.notApplicable)}
            data-testid="utilization-na"
          >
            {t('states.notApplicable')}
          </span>
        )}
      </div>
      <ProgressBar
        value={applicable ? clamped / 100 : 0}
        max={1}
        thickness="large"
        aria-label={label}
        aria-valuetext={applicable ? `${clamped.toFixed(1)}%` : t('states.notApplicable')}
      />
      {caption ? <span className={styles.caption}>{caption}</span> : null}
      {detail ? <span className={styles.detail}>{detail}</span> : null}
    </section>
  );
};

UtilizationCard.displayName = 'UtilizationCard';
